import { Play, Film, Sparkles, SearchX, Heart } from "lucide-react";
import { fileUrl } from "@/lib/api";

const BADGE_STYLES = {
  Trending: "bg-gold-500 text-ink-950",
  Exclusive: "border border-gold-400/60 bg-ink-950/70 text-gold-300",
  "Coming Soon": "border border-stone-500/50 bg-ink-950/70 text-stone-300",
  "Official Launch": "bg-gradient-to-r from-gold-600 to-gold-400 text-ink-950",
};

function ShowCard({ show, onTrailer, liked, onLike }) {
  return (
    <article
      data-testid={`show-card-${show.slug}`}
      className={`group relative overflow-hidden rounded-2xl bg-ink-900 transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_60px_rgba(0,0,0,0.65)] ${
        show.is_launch
          ? "border border-gold-500/60 ring-1 ring-gold-500/40"
          : "border border-stone-800/80 hover:border-gold-500/40"
      }`}
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        {show.poster_url ? (
          <img
            src={fileUrl(show.poster_url)}
            alt={show.title}
            data-testid={`show-poster-${show.slug}`}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-ink-800 text-gold-500/60">
            <Film className="h-10 w-10" />
            <span className="text-[10px] uppercase tracking-[0.3em]">Poster Coming Soon</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/20 to-transparent" />

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {show.access === "premium" && (
            <span data-testid={`show-premium-badge-${show.slug}`} className="rounded-full bg-gradient-to-r from-gold-600 to-gold-400 px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] text-ink-950">
              Premium
            </span>
          )}
          {(show.badges || []).map((badge) => (
            <span
              key={badge}
              data-testid={`show-badge-${show.slug}-${badge.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-3 py-1 text-[9px] font-bold uppercase tracking-[0.16em] ${
                BADGE_STYLES[badge] || "border border-gold-500/40 bg-ink-950/70 text-gold-300"
              }`}
            >
              {badge}
            </span>
          ))}
        </div>

        <button
          data-testid={`show-like-${show.slug}`}
          aria-label={`Like ${show.title}`}
          onClick={() => onLike(show.slug, show.title)}
          className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-stone-600/70 bg-ink-950/80 text-stone-300 backdrop-blur transition-all duration-300 hover:border-red-500/70 hover:text-red-400"
        >
          <Heart className={`h-4 w-4 ${liked.has(show.slug) ? "fill-red-500 text-red-400" : ""}`} />
        </button>

        <div className="absolute inset-0 z-10 flex items-center justify-center opacity-100 transition-all duration-500 lg:opacity-0 lg:group-hover:opacity-100">
          <div className="absolute inset-0 bg-ink-950/55 backdrop-blur-[2px]" />
          <button
            data-testid={`show-play-preview-${show.slug}`}
            aria-label={`Play preview of ${show.title}`}
            onClick={() => onTrailer(show)}
            className="relative flex h-16 w-16 items-center justify-center rounded-full border border-gold-400/70 bg-ink-950/80 text-gold-300 transition-all duration-300 hover:scale-110 hover:bg-gold-500 hover:text-ink-950 hover:shadow-[0_0_32px_rgba(212,175,55,0.6)]"
          >
            <Play className="h-6 w-6 fill-current" />
          </button>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-5">
          <h3 className="font-display text-xl font-bold text-stone-50">{show.title}</h3>
          <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-stone-400">{show.category}</p>
        </div>
      </div>
    </article>
  );
}

export default function ShowsSection({ shows, allCount, query, onTrailer, onClearQuery, liked, onLike }) {
  return (
    <section id="shows" data-testid="shows-section" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div>
            <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold-500">
              <Sparkles className="h-4 w-4" /> Only on VIEWNA
            </p>
            <h2 className="mt-4 font-display text-4xl font-black text-stone-50 sm:text-5xl">
              Featured <span className="gold-text">Shows</span>
            </h2>
          </div>
          <div className="hidden h-px min-w-[120px] flex-1 bg-gradient-to-r from-gold-500/50 to-transparent sm:block" />
          <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500">
            {shows.length} of {allCount} titles
          </p>
        </div>

        {shows.length === 0 ? (
          <div
            data-testid="shows-empty-state"
            className="mt-16 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-gold-500/25 py-20 text-stone-400"
          >
            <SearchX className="h-10 w-10 text-gold-500/60" />
            <p>No shows match &ldquo;{query}&rdquo;.</p>
            <button
              data-testid="shows-clear-search-button"
              onClick={onClearQuery}
              className="text-gold-400 underline underline-offset-4 transition-colors hover:text-gold-300"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {shows.map((show) => (
              <ShowCard key={show.slug || show.id} show={show} onTrailer={onTrailer} liked={liked} onLike={onLike} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
